import { jsxs, jsx } from 'react/jsx-runtime';
import { useQueryResourceList } from '../../../queries/useQueryResourceList.js';
import { useWoody } from '../../../../woody/woody.js';
import { LayoutGroup } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { styled } from 'styled-components';
import { useDebouncedCallback } from '@fabric-app/hooks/useDebouncedCallback';
import { useSocketIOParentRoomEventListener } from '../../../../socket.io/ParentRoom/useSocketIOParentRoomEventListener.js';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { useMutationRenameColorLabel } from '../../../../labels/mutations/useMutationRenameColorLabel.js';
import { useMutationChangeResourceLabel } from '../../../../resource-detail/mutations/useMutationChangeResourceLabel.js';
import Skeleton from '@fabric-app/ui/components/Skeleton';
import { VirtualResourceGrid } from '../ResourceGrid/VirtualResourceGrid.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';

const ResourceColorLabelColumn = ({ colorLabel, folderId, disableItemPreviewContextMenu, resourcePreviewProps, layoutId, onResourceClick, canModifyContent, virtualGridOptions, queryParams }) => {
  const { client } = useWoody();
  const columnContainerRef = useRef(null);
  const { resources, total: resourcesTotalCount, isFetchingNextPage, isLoading, hasNextPage, fetchNextPage, refetch } = useQueryResourceList({
    ...queryParams,
    label: colorLabel?.id || null
  }, {
    refetchInterval: false
  });
  useSocketIOParentRoomEventListener("RESOURCES:RELOAD", () => {
    refetch();
  });
  const onInfiniteTrigger = useDebouncedCallback(!isFetchingNextPage && !isLoading && hasNextPage ? fetchNextPage : void 0, 100);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  useEffect(() => {
    if (!columnContainerRef.current) return;
    const onDragOver = () => {
      setIsDraggingOver(true);
    };
    const onDragOut = () => {
      setIsDraggingOver(false);
    };
    columnContainerRef.current.addEventListener("object-drag-over", onDragOver);
    columnContainerRef.current.addEventListener("object-drag-out", onDragOut);
    return () => {
      columnContainerRef.current?.removeEventListener("object-drag-over", onDragOver);
      columnContainerRef.current?.removeEventListener("object-drag-out", onDragOut);
    };
  }, [
    columnContainerRef
  ]);
  const { mutate: mutateChangeResourceLabel } = useMutationChangeResourceLabel();
  useEffect(() => {
    if (!columnContainerRef.current) return;
    const onDragDrop = async (e) => {
      const { objectId: resourceId } = e.detail;
      setIsDraggingOver(false);
      mutateChangeResourceLabel({
        resourceId,
        label: colorLabel
      });
    };
    columnContainerRef.current.addEventListener("object-drag-drop", onDragDrop);
    return () => columnContainerRef.current?.removeEventListener("object-drag-drop", onDragDrop);
  }, [
    client,
    colorLabel,
    columnContainerRef,
    mutateChangeResourceLabel,
    folderId
  ]);
  const { mutate: mutateRenameColorLabel } = useMutationRenameColorLabel();
  const titleInputControls = useInputControls(colorLabel?.name, {
    debounce: 300,
    trim: true,
    onChange: (value) => {
      mutateRenameColorLabel({
        folderId,
        colorLabelId: colorLabel?.id ?? 0,
        newTitle: value
      });
    }
  });
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };
  return /* @__PURE__ */ jsxs(ColumnContainer, {
    ref: columnContainerRef,
    "data-droppable": true,
    children: [
      isDraggingOver && /* @__PURE__ */ jsx(DragOverElement, {}),
      /* @__PURE__ */ jsxs(ColumnHeader, {
        children: [
          colorLabel && /* @__PURE__ */ jsx(ColorLabelDot, {
            style: {
              background: colorLabel.hexColor
            }
          }),
          /* @__PURE__ */ jsxs(ColumnTitle, {
            children: [
              isLoading ? /* @__PURE__ */ jsx(Skeleton, {
                width: 16,
                height: 16,
                borderRadius: 4
              }) : /* @__PURE__ */ jsxs("span", {
                children: [
                  "(",
                  resourcesTotalCount,
                  ")"
                ]
              }),
              colorLabel ? /* @__PURE__ */ jsx(Input, {
                type: "text",
                value: titleInputControls.value,
                onChange: titleInputControls.onChange,
                placeholder: "Title",
                onKeyDown: handleKeyPress
              }) : "Unsorted"
            ]
          })
        ]
      }),
      /* @__PURE__ */ jsx(SortModeScrollArea, {
        innerDisplayFlex: true,
        children: /* @__PURE__ */ jsx(LayoutGroup, {
          id: `${layoutId}--sort`,
          children: /* @__PURE__ */ jsx(VirtualResourceGrid, {
            zoomLevel: 1,
            resources,
            resourcesTotalCount,
            disableItemPreviewContextMenu,
            canMultiSelect: false,
            disableDrag: !canModifyContent,
            scrollYMargin: 0,
            containerWidth: 260,
            colorLabels: void 0,
            onInfiniteTrigger,
            virtualGridOptions,
            resourcePreviewProps,
            onResourceClick
          })
        })
      })
    ]
  });
};
const SortModeScrollArea = styled(ScrollArea.Default).attrs((p) => {
  return {
    ...p,
    innerDisplayFlex: true
  };
}).withConfig({
  displayName: "ResourceColorLabelColumn__SortModeScrollArea",
  componentId: "sc-cf532628-0"
})([
  `& > div[data-radix-scroll-area-viewport] > div{align-items:center;padding-top:1rem;padding-bottom:1rem;}`
]);
const ColumnContainer = styled.div.withConfig({
  displayName: "ResourceColorLabelColumn__ColumnContainer",
  componentId: "sc-cf532628-1"
})([
  `overflow:hidden;gap:0;position:relative;flex-shrink:0;width:310px;height:100%;background:rgba(0,0,0,0.01);border:1px solid var(--fabric-color-border-secondary);border-radius:10px;display:flex;flex-direction:column;`
]);
const DragOverElement = styled.div.withConfig({
  displayName: "ResourceColorLabelColumn__DragOverElement",
  componentId: "sc-cf532628-2"
})([
  `position:absolute;will-change:transform;background:rgba(51,0,255,0.05);inset:0;z-index:5;`
]);
const ColumnHeader = styled.div.withConfig({
  displayName: "ResourceColorLabelColumn__ColumnHeader",
  componentId: "sc-cf532628-3"
})([
  `background:`,
  `;display:flex;align-items:center;height:50px;padding:0 1rem;border-bottom:1px solid `,
  `;gap:0.75rem;flex-shrink:0;`
], cssVar["color-bg-tertiary"], cssVar["color-border-primary"]);
const ColorLabelDot = styled.div.withConfig({
  displayName: "ResourceColorLabelColumn__ColorLabelDot",
  componentId: "sc-cf532628-4"
})([
  `height:0.5rem;width:0.5rem;border-radius:50%;flex-shrink:0;`
]);
const ColumnTitle = styled.div.withConfig({
  displayName: "ResourceColorLabelColumn__ColumnTitle",
  componentId: "sc-cf532628-5"
})([
  `font-size:1rem;font-weight:500;color:`,
  `;display:flex;align-items:center;gap:0.25rem;`
], cssVar["color-text-tertiary"]);
const Input = styled.input.withConfig({
  displayName: "ResourceColorLabelColumn__Input",
  componentId: "sc-cf532628-6"
})([
  `background:transparent;color:`,
  `;outline:none;text-transform:capitalize;`
], cssVar["color-text-primary"]);

export { ResourceColorLabelColumn };
//# sourceMappingURL=ResourceColorLabelColumn.js.map
